import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const plans = [
  {
    name: "Free",
    monthly: "$0",
    annualMonthly: "$0",
    annualTotal: "$0",
    period: "forever",
    annualPeriod: "forever",
    description: "Try BotSweep on a personal project.",
    features: [
      "1 domain",
      "10,000 requests/month",
      "3-day data retention",
      "Basic bot detection",
      "Community support",
    ],
    cta: "Start Free",
    popular: false,
    priceId: null,
    annualPriceId: null,
  },
  {
    name: "Starter",
    monthly: "$49",
    annualMonthly: "$41",
    annualTotal: "$490",
    period: "/mo",
    annualPeriod: "/mo",
    description: "For growing sites that need real protection.",
    features: [
      "3 domains",
      "500,000 requests/month",
      "30-day data retention",
      "Advanced detection + risk scoring",
      "Real-time dashboard",
      "AI crawler detection & controls",
      "CSV export",
      "Email spike alerts",
      "Email support",
    ],
    cta: "Start Free Trial",
    popular: true,
    priceId: "price_1TFyb7FgX15XrqKDuP2ck2cC",
    annualPriceId: "price_1TG2PqFgX15XrqKDoMMJoURL",
  },
  {
    name: "Pro",
    monthly: "$99",
    annualMonthly: "$83",
    annualTotal: "$990",
    period: "/mo",
    annualPeriod: "/mo",
    description: "For businesses serious about traffic quality.",
    features: [
      "10 domains",
      "2M requests/month",
      "90-day data retention",
      "Everything in Starter",
      "Advanced AI traffic analytics",
      "Priority support",
      "Early access to new features",
    ],
    cta: "Start Free Trial",
    popular: false,
    priceId: "price_1TFyc6FgX15XrqKDA6q7J05C",
    annualPriceId: "price_1TG2QeFgX15XrqKD6N7XFDzO",
  },
  {
    name: "Agency",
    monthly: "$249",
    annualMonthly: "$208",
    annualTotal: "$2,490",
    period: "/mo",
    annualPeriod: "/mo",
    description: "For agencies managing multiple clients.",
    features: [
      "Unlimited domains",
      "10M requests/month",
      "1-year data retention",
      "Everything in Pro",
      "AI traffic API access",
      "Dedicated account manager",
      "SLA guarantee",
      "Custom onboarding",
    ],
    cta: "Contact Sales",
    popular: false,
    priceId: "price_1TFycWFgX15XrqKDfr3bIGqH",
    annualPriceId: "price_1TG2RvFgX15XrqKD4IuPijJ7",
  },
];

const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCheckout = async (plan: typeof plans[0]) => {
    if (!plan.priceId) {
      navigate("/auth");
      return;
    }

    if (plan.name === "Agency") {
      window.location.href = "mailto:support@botsweep.com?subject=BotSweep Agency Plan";
      return;
    }

    setLoadingPlan(plan.name);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setLoadingPlan(null);
        navigate("/auth");
        return;
      }

      const result: any = await Promise.race([
        supabase.functions.invoke("create-checkout", {
          body: { priceId: annual && plan.annualPriceId ? plan.annualPriceId : plan.priceId },
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Request timed out — please try again")), 30000)
        ),
      ]);

      console.log("[Pricing] create-checkout response:", result);

      const { data, error } = result;
      if (error) throw error;

      const url = data?.url || data?.data?.url;
      if (!url) throw new Error("No checkout URL in response");

      console.log("[Pricing] Redirecting to:", url);
      const inIframe = window.top !== window.self;
      console.log("[Pricing] In iframe:", inIframe);

      try {
        if (inIframe && window.top) {
          window.top.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch (navErr) {
        console.warn("[Pricing] Top-level nav blocked, opening new tab", navErr);
        window.open(url, "_blank");
      }
    } catch (err: any) {
      console.error("[Pricing] Checkout error:", err);
      toast.error(err?.message || "Something went wrong, please try again");
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="relative py-24 md:py-32 px-6">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <p className="text-[10px] font-mono text-primary uppercase tracking-[0.3em] mb-4">
            Pricing
          </p>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl text-foreground tracking-tight mb-3">
            Simple. Honest.
          </h2>
          <p className="text-sm text-muted-foreground font-mono">
            Start free. Upgrade when ready. Cancel anytime.
          </p>
        </motion.div>

        {/* Annual/Monthly Toggle */}
        <div className="flex items-center justify-start gap-3 mb-12">
          <span className={`text-[11px] font-mono transition-colors ${!annual ? "text-foreground" : "text-foreground/60"}`}>Monthly</span>
          <button
            onClick={() => setAnnual(!annual)}
            aria-label="Toggle annual billing"
            className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${annual ? "bg-primary" : "bg-muted"}`}
          >
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-foreground transition-transform duration-300 ${annual ? "translate-x-7" : "translate-x-1"}`} />
          </button>
          <span className={`text-[11px] font-mono transition-colors ${annual ? "text-foreground" : "text-foreground/60"}`}>Annual</span>
          {annual && (
            <Badge className="bg-primary/10 text-primary border-primary/20 text-[9px] font-mono">
              Save ~17% · 2 months free
            </Badge>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative"
            >
              {plan.popular && (
                <div className="absolute -top-3 left-6 z-10">
                  <span className="bg-primary text-primary-foreground text-[8px] font-mono uppercase tracking-[0.2em] px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              <div
                className={`h-full flex flex-col rounded-lg p-8 transition-all duration-300 ${
                  plan.popular
                    ? "gradient-border-animated glow-cyan-strong hover:shadow-[0_0_50px_hsl(191_100%_50%/0.15)]"
                    : "bg-card border border-border hover:border-primary/20 hover:-translate-y-0.5 hover:shadow-[0_0_30px_hsl(191_100%_50%/0.08)] hover:bg-card/80"
                }`}
              >
                <div className="mb-8">
                  <h3 className="font-display font-bold text-base text-foreground mb-1">{plan.name}</h3>
                  <p className="text-[11px] text-muted-foreground font-mono">{plan.description}</p>
                </div>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="font-data text-5xl font-bold text-foreground tracking-wider">
                    {annual ? plan.annualMonthly : plan.monthly}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{annual ? plan.annualPeriod : plan.period}</span>
                </div>
                {annual && plan.name !== "Free" && (
                  <p className="text-[10px] font-mono text-muted-foreground mb-8">
                    {plan.annualTotal}/yr · 2 months free
                  </p>
                )}
                {(!annual || plan.name === "Free") && <div className="mb-10" />}

                <ul className="space-y-3.5 mb-10 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                      <span className="text-[11px] text-muted-foreground font-mono">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleCheckout(plan)}
                  disabled={loadingPlan === plan.name}
                  className={`w-full h-11 text-[11px] font-mono uppercase tracking-[0.1em] ${
                    plan.popular
                      ? "btn-shimmer bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  {loadingPlan === plan.name ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : null}
                  {loadingPlan === plan.name ? "Redirecting..." : plan.cta}
                  {loadingPlan !== plan.name && <ArrowRight className="w-3 h-3 ml-1" />}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-foreground/60 font-mono mt-8">
          All plans include a 14-day free trial. No credit card required.
        </p>
      </div>
    </section>
  );
};

export default Pricing;
